import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Zap, Hand, Check, CheckCheck, Clock, XCircle, Ban } from 'lucide-react';

interface MessageLog {
    id: string;
    source: 'automatic' | 'manual';
    gym: { id: string; name: string } | null;
    campaignName: string | null;
    templateName: string | null;
    templateTitle: string | null;
    triggerEvent: string;
    messageType: string | null;
    phoneNumber: string;
    memberName: string | null;
    status: string;
    errorMessage: string | null;
    providerMessageId: string | null;
    triggeredAt: string | null;
    sentAt: string | null;
    deliveredAt: string | null;
    seenAt: string | null;
    totalCost: number | null;
}

interface FilterOptions {
    gyms: { id: string; name: string }[];
    templates: string[];
}

interface Props {
    token: string;
}

const getApiBaseUrl = () => {
    let url = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    return url.endsWith('/') ? url.slice(0, -1) : url;
};

const API_BASE_URL = getApiBaseUrl();

const STATUS_BADGES: Record<string, string> = {
    PENDING: 'bg-gray-100 text-gray-600',
    SENT: 'bg-blue-100 text-blue-700',
    DELIVERED: 'bg-teal-100 text-teal-700',
    READ: 'bg-green-100 text-green-700',
    FAILED: 'bg-red-100 text-red-700',
    OPTED_OUT: 'bg-amber-100 text-amber-700'
};

const STATUS_OPTIONS = ['PENDING', 'SENT', 'DELIVERED', 'READ', 'FAILED', 'OPTED_OUT'];

export default function MessageLogsTab({ token }: Props) {
    const [page, setPage] = useState(1);
    const limit = 25;
    const [data, setData] = useState<{ data: MessageLog[]; pagination: any } | null>(null);
    const [filterOptions, setFilterOptions] = useState<FilterOptions>({ gyms: [], templates: [] });
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const [gymId, setGymId] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [type, setType] = useState('');
    const [template, setTemplate] = useState('');
    const [status, setStatus] = useState('');

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/saas/message-logs/filters`, {
            headers: { Authorization: `Bearer ${token}`, 'x-client-type': 'web' }
        })
            .then(r => r.json())
            .then(json => { if (json.success) setFilterOptions(json.data); })
            .catch(() => { /* ignore */ });
    }, [token]);

    const fetchLogs = useCallback(async (silent = false) => {
        if (!silent) setLoading(true); else setRefreshing(true);
        try {
            const params = new URLSearchParams({ page: String(page), limit: String(limit) });
            if (gymId) params.set('gymId', gymId);
            if (from) params.set('from', from);
            if (to) params.set('to', to);
            if (type) params.set('type', type);
            if (template) params.set('template', template);
            if (status) params.set('status', status);

            const res = await fetch(`${API_BASE_URL}/api/saas/message-logs?${params}`, {
                headers: { Authorization: `Bearer ${token}`, 'x-client-type': 'web' }
            });
            const json = await res.json();
            if (json.success) setData(json);
        } catch { /* ignore */ }
        finally { setLoading(false); setRefreshing(false); }
    }, [token, page, gymId, from, to, type, template, status]);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    const resetToFirstPage = (setter: (v: string) => void) => (v: string) => { setter(v); setPage(1); };
    const onGym = resetToFirstPage(setGymId);
    const onFrom = resetToFirstPage(setFrom);
    const onTo = resetToFirstPage(setTo);
    const onType = resetToFirstPage(setType);
    const onTemplate = resetToFirstPage(setTemplate);
    const onStatus = resetToFirstPage(setStatus);

    const logs = data?.data ?? [];
    const total = data?.pagination?.total || 0;
    const totalPages = data?.pagination?.totalPages || 0;

    const fmt = (d: string | null) => d
        ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }).format(new Date(d))
        : null;

    const hasActiveFilters = gymId || from || to || type || template || status;

    const lifecycleSteps = (log: MessageLog) => [
        { label: 'Triggered', at: log.triggeredAt, icon: Clock, done: !!log.triggeredAt },
        { label: 'Sent', at: log.sentAt, icon: Check, done: !!log.sentAt || ['SENT', 'DELIVERED', 'READ'].includes(log.status) },
        { label: 'Delivered', at: log.deliveredAt, icon: CheckCheck, done: !!log.deliveredAt || ['DELIVERED', 'READ'].includes(log.status) },
        { label: 'Read', at: log.seenAt, icon: CheckCheck, done: !!log.seenAt || log.status === 'READ' },
    ];

    const selectCls = "border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500";

    if (loading && !data) return (
        <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
    );

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
                <button
                    onClick={() => fetchLogs(true)}
                    disabled={refreshing}
                    className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                    title="Refresh"
                >
                    <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin text-indigo-600' : ''}`} />
                </button>
                <span className="text-sm font-medium text-gray-500">Message Lifecycle Logs ({total})</span>
            </div>

            {/* Filters */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex flex-wrap items-end gap-3">
                <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Gym</label>
                    <select value={gymId} onChange={e => onGym(e.target.value)} className={selectCls}>
                        <option value="">All gyms</option>
                        {filterOptions.gyms.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">From</label>
                    <input type="date" value={from} onChange={e => onFrom(e.target.value)} className={selectCls} />
                </div>
                <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">To</label>
                    <input type="date" value={to} onChange={e => onTo(e.target.value)} className={selectCls} />
                </div>
                <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Type</label>
                    <select value={type} onChange={e => onType(e.target.value)} className={selectCls}>
                        <option value="">All types</option>
                        <option value="automatic">Automatic</option>
                        <option value="manual">Manual campaign</option>
                    </select>
                </div>
                <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Template</label>
                    <select value={template} onChange={e => onTemplate(e.target.value)} className={selectCls}>
                        <option value="">All templates</option>
                        {filterOptions.templates.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Status</label>
                    <select value={status} onChange={e => onStatus(e.target.value)} className={selectCls}>
                        <option value="">All statuses</option>
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                    </select>
                </div>
                {hasActiveFilters && (
                    <button
                        onClick={() => { setGymId(''); setFrom(''); setTo(''); setType(''); setTemplate(''); setStatus(''); setPage(1); }}
                        className="text-sm text-indigo-600 hover:text-indigo-800 font-medium pb-1.5"
                    >
                        Clear filters
                    </button>
                )}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {['Gym', 'Recipient', 'Template', 'Type', 'Status', 'Lifecycle', 'Details'].map(h => (
                                    <th key={h} className="px-5 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {logs.map(log => (
                                <tr key={log.id} className="hover:bg-indigo-50/30 transition-colors align-top">
                                    <td className="px-5 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{log.gym?.name || '—'}</td>
                                    <td className="px-5 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{log.memberName || '—'}</div>
                                        <div className="text-xs text-gray-500">{log.phoneNumber}</div>
                                    </td>
                                    <td className="px-5 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{log.templateName || (log.source === 'automatic' ? log.triggerEvent : '—')}</div>
                                        <div className="text-xs text-gray-500">
                                            {log.source === 'manual' ? (log.campaignName || '') : log.triggerEvent}
                                        </div>
                                    </td>
                                    <td className="px-5 py-4 whitespace-nowrap">
                                        {log.source === 'automatic' ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700">
                                                <Zap className="h-3 w-3" />
                                                Automatic
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-700">
                                                <Hand className="h-3 w-3" />
                                                Manual
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-5 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_BADGES[log.status] || 'bg-gray-100 text-gray-600'}`}>
                                            {log.status === 'FAILED' && <XCircle className="h-3 w-3" />}
                                            {log.status === 'OPTED_OUT' && <Ban className="h-3 w-3" />}
                                            {log.status.replace('_', ' ')}
                                        </span>
                                    </td>
                                    <td className="px-5 py-4">
                                        {['FAILED', 'OPTED_OUT'].includes(log.status) ? (
                                            <span className="text-xs text-gray-400">{fmt(log.triggeredAt) || '—'}</span>
                                        ) : (
                                            <div className="flex items-center gap-0">
                                                {lifecycleSteps(log).map((step, i) => (
                                                    <div key={step.label} className="flex items-center">
                                                        {i > 0 && <div className={`h-px w-4 ${step.done ? 'bg-green-400' : 'bg-gray-200'}`} />}
                                                        <div
                                                            className="flex flex-col items-center gap-0.5 px-0.5"
                                                            title={step.at ? `${step.label}: ${fmt(step.at)}` : `${step.label}: not yet`}
                                                        >
                                                            <step.icon className={`h-3.5 w-3.5 ${step.done ? 'text-green-600' : 'text-gray-300'}`} />
                                                            <span className={`text-[9px] font-semibold uppercase tracking-wide ${step.done ? 'text-green-700' : 'text-gray-300'}`}>{step.label}</span>
                                                            <span className="text-[9px] text-gray-400 whitespace-nowrap">{step.at ? fmt(step.at) : ''}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-5 py-4 text-xs text-gray-500 max-w-[220px]">
                                        {log.errorMessage
                                            ? <span className="text-red-600 truncate block" title={log.errorMessage}>{log.errorMessage}</span>
                                            : (log.providerMessageId
                                                ? <span className="truncate block text-gray-400" title={log.providerMessageId}>{log.providerMessageId}</span>
                                                : '—')}
                                    </td>
                                </tr>
                            ))}
                            {logs.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center text-gray-400 text-sm">
                                        {hasActiveFilters ? 'No messages match the selected filters.' : 'No messages recorded yet.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {totalPages > 1 && (
                <div className="flex items-center justify-between bg-white px-4 py-3 rounded-xl border border-gray-200 shadow-sm">
                    <p className="text-sm text-gray-700">
                        Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
                    </p>
                    <div className="flex gap-1">
                        <button onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1} className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40">Prev</button>
                        <button onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page === totalPages} className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40">Next</button>
                    </div>
                </div>
            )}
        </div>
    );
}
